/**
 * The CSV import contract, shared by every module (L-10, L-11; ADR-74, ADR-75).
 *
 * CREATE-ONLY. A module contributes its columns (`property-import-columns.ts`,
 * `tenant-org-import-columns.ts`) and its write key; everything else — the
 * header rules, the findings, the preview, the confirmation and the result —
 * is one contract, so the two imports cannot drift into two answers to one
 * question. Each template's columns are that module's create form under the
 * form's own labels, and every rule a cell must satisfy is the create schema's.
 */

import { z } from 'zod';

import { PROPERTY_IMPORT_COLUMNS } from './property-import-columns.ts';
import { toCsv } from './table-export.ts';
import { TENANCY_IMPORT_COLUMNS } from './tenancy-import-columns.ts';
import { TENANT_ORG_IMPORT_COLUMNS } from './tenant-org-import-columns.ts';

/**
 * One file holds at most this many data rows, in every module (Matt,
 * 2026-10-12, PR #13 review round 1; L-11 "same decisions"). Measured for
 * properties: 2,000 rows held the authority lock for about 97 s at execute;
 * 300 rows for about 9 s.
 */
export const IMPORT_ROW_CAP = 500;

export interface ImportColumn<F extends string = string> {
  readonly header: string;
  readonly field: F;
  readonly required: boolean;
  /** What a valid cell looks like, in the words a refusal uses. */
  readonly rule: string;
  /** The template's example row. */
  readonly example: string;
  /** A finding's sentence where the generic one would read badly for this column. */
  readonly messages?: Partial<Record<ImportErrorCode, string>>;
}

/**
 * The modules an import can create, each named by its AUDIT ENTITY TYPE — the
 * value a run's worksheet, its ADR-67 batch and its duplicate candidates carry
 * (0087, 0088), so one vocabulary names the module everywhere.
 */
export const IMPORT_MODULES = ['property', 'tenant_org', 'tenancy'] as const;
export type ImportModule = (typeof IMPORT_MODULES)[number];

export interface ImportModuleSpec {
  readonly module: ImportModule;
  /** The choice on the screen. */
  readonly label: string;
  readonly singular: string;
  readonly plural: string;
  /**
   * The keys every route of this module requires, beside `data.import.execute`
   * — the writes each row performs (L-12: a tenancy and its lease are two).
   */
  readonly writeKeys: readonly string[];
  /** The path segment of the upload and template routes. */
  readonly slug: 'properties' | 'tenant-orgs' | 'tenancies';
  readonly templateFilename: string;
  readonly columns: readonly ImportColumn[];
  /** The REQUIRED column that names a row on the preview and in the skipped list. */
  readonly nameField: string;
  /** The column a row is known by besides its name, if the module has one. */
  readonly keyField: string | null;
  /** Where a created record opens. */
  readonly recordPath: string;
}

export const IMPORT_MODULE_SPECS: Readonly<Record<ImportModule, ImportModuleSpec>> = {
  property: {
    module: 'property',
    label: 'Properties',
    singular: 'property',
    plural: 'properties',
    writeKeys: ['properties.create'],
    slug: 'properties',
    templateFilename: 'propos-properties-import-template.csv',
    columns: PROPERTY_IMPORT_COLUMNS,
    nameField: 'name',
    keyField: 'yardiId',
    recordPath: '/properties/:id',
  },
  tenant_org: {
    module: 'tenant_org',
    label: 'Tenant organizations',
    singular: 'tenant organization',
    plural: 'tenant organizations',
    writeKeys: ['tenants.create'],
    slug: 'tenant-orgs',
    templateFilename: 'propos-tenant-organizations-import-template.csv',
    columns: TENANT_ORG_IMPORT_COLUMNS,
    nameField: 'name',
    keyField: null,
    recordPath: '/tenants/:id',
  },
  tenancy: {
    module: 'tenancy',
    label: 'Tenancies and leases',
    singular: 'tenancy',
    plural: 'tenancies',
    writeKeys: ['tenants.create', 'leases.manage'],
    slug: 'tenancies',
    templateFilename: 'propos-tenancies-leases-import-template.csv',
    columns: TENANCY_IMPORT_COLUMNS,
    nameField: 'tenantOrganization',
    keyField: 'yardiTenantId',
    recordPath: '/tenancies/:id',
  },
};

/** Where a created record of this module opens. */
export function importRecordPath(module: ImportModule, id: string): string {
  return IMPORT_MODULE_SPECS[module].recordPath.replace(':id', encodeURIComponent(id));
}

/**
 * RECOGNIZED BUT NOT IMPORTED (Matt, 2026-10-12). No template carries it; a
 * file that does is read, and any row with a value in it is refused —
 * "updates are not yet supported" — rather than created as a new record beside
 * the one it names. L-33 is where update-by-import arrives.
 */
export const PROPOS_ID_HEADER = 'PropOS ID';

/**
 * Columns a file may not carry at all, refused by name (docs/18 §7e): setting a
 * status is a lifecycle transition wearing a column.
 */
export const IMPORT_LIFECYCLE_HEADERS: readonly string[] = ['Status'];

/**
 * The example row is ignored on upload, recognized by its Name. A real record
 * beginning with these words is implausible enough that the plainer marker
 * wins over a hidden one.
 */
export const IMPORT_EXAMPLE_PREFIX = 'Example —';

/**
 * A module's template: the header row and one example row. Built through
 * `toCsv`, so it carries the same formula safety and BOM as every operational
 * export.
 */
export function importTemplateCsv(module: ImportModule): string {
  const { columns, nameField } = IMPORT_MODULE_SPECS[module];
  const example = columns.map((column) =>
    column.field === nameField
      ? `${IMPORT_EXAMPLE_PREFIX} PropOS ignores this row; delete it or leave it`
      : column.example,
  );
  return toCsv(
    columns.map((column) => column.header),
    [example],
  );
}

/** Normalizes a header the way the reader compares them: case and spaces only. */
export function normalizeImportHeader(header: string): string {
  return header.trim().replace(/\s+/g, ' ').toLowerCase();
}

// ---------------------------------------------------------------------------
// Findings — bounded codes, never free text (0087's `import_findings`)
// ---------------------------------------------------------------------------

export const IMPORT_ERROR_CODES = [
  'required_missing',
  'invalid_value',
  'yardi_id_taken',
  'yardi_id_repeated',
  'propos_id_not_supported',
  'reference_not_found',
  'reference_inactive',
  'reference_ambiguous',
  'address_incomplete',
  'not_permitted',
  // L-12: tenancies and leases.
  'yardi_tenant_id_taken',
  'yardi_tenant_id_repeated',
  'active_tenancy_exists',
  'active_tenancy_repeated',
  'needs_lease',
] as const;
export type ImportErrorCode = (typeof IMPORT_ERROR_CODES)[number];

export const IMPORT_WARNING_CODES = [
  'zip_truncated',
  'scientific_notation',
  'precision_loss',
  'encoding_damage',
  'formula_like_text',
  // L-12: a date not written as YYYY-MM-DD (M/D/YYYY is accepted, decision 4).
  'ambiguous_date',
] as const;
export type ImportWarningCode = (typeof IMPORT_WARNING_CODES)[number];

export interface ImportFinding {
  readonly code: ImportErrorCode | ImportWarningCode;
  /** A field of the run's module (`ImportColumn.field`), or null for the row. */
  readonly field: string | null;
}

/** The sentence for a finding. Generated from the code, never stored (0087). */
export function importFindingMessage(module: ImportModule, finding: ImportFinding): string {
  const spec = IMPORT_MODULE_SPECS[module];
  const column = spec.columns.find((c) => c.field === finding.field);
  const header = column?.header ?? 'This row';
  const rule = column?.rule;
  const own = column?.messages?.[finding.code as ImportErrorCode];
  if (own !== undefined) return own;
  switch (finding.code) {
    case 'required_missing':
      return `${header} is required`;
    case 'invalid_value':
      return rule === undefined ? `${header} is not valid` : `${header}: enter ${rule}`;
    case 'yardi_id_taken':
      return 'This Yardi ID already belongs to a property in PropOS';
    case 'yardi_id_repeated':
      return 'This Yardi ID appears on more than one row of the file';
    case 'propos_id_not_supported':
      return `This row names an existing PropOS ${spec.singular} — updates are not yet supported`;
    case 'reference_not_found':
      return `${header}: no ${header.toLowerCase()} has this name`;
    case 'reference_inactive':
      return `${header}: this ${header.toLowerCase()} is retired`;
    case 'reference_ambiguous':
      return `${header}: more than one active ${header.toLowerCase()} has this name`;
    case 'address_incomplete':
      return 'An address needs at least a street, a city or a ZIP';
    case 'not_permitted':
      return `Creating ${spec.plural} needs ${spec.writeKeys.join(' and ')}, and the property in your scope — ask an administrator`;
    case 'yardi_tenant_id_taken':
      return 'This Yardi tenant ID already belongs to a tenancy in PropOS';
    case 'yardi_tenant_id_repeated':
      return 'This Yardi tenant ID appears on more than one row of the file';
    case 'active_tenancy_exists':
      return 'This organization already has an active tenancy at this property — updates are not yet supported';
    case 'active_tenancy_repeated':
      return 'More than one row of the file makes this organization active at this property';
    case 'needs_lease':
      return `${header} needs a lease on the same row`;
    case 'zip_truncated':
      return 'ZIP has fewer than 5 digits — a spreadsheet may have dropped a leading zero';
    case 'scientific_notation':
      return `${header} looks like scientific notation — a spreadsheet may have changed it`;
    case 'precision_loss':
      return `${header} is too long a number for a spreadsheet to keep exactly`;
    case 'encoding_damage':
      return `${header} contains a replacement character — the file's text may be damaged`;
    case 'ambiguous_date':
      return `${header} is not written as YYYY-MM-DD — check a spreadsheet did not reformat it`;
    case 'formula_like_text':
      return `${header} begins like a spreadsheet formula; it is imported as text, unchanged`;
  }
}

// ---------------------------------------------------------------------------
// The preview, the confirmation and the result
// ---------------------------------------------------------------------------

export type ImportRowStatus = 'ready' | 'possible_duplicate' | 'error';

export type ImportMatchReason =
  | 'exact_normalized_name'
  | 'trigram_similarity'
  | 'same_address'
  | 'inactive_match'
  // L-12: the record's business key — for a tenancy, the same organization at
  // the same property. One of 0087's installed codes, so no schema change.
  | 'same_business_key';

/**
 * What a possible duplicate matched: a record already in PropOS (`recordId`,
 * of the run's module), or an EARLIER row of the same file (`rowNo`) — two rows
 * that would each pass alone and then collide with each other once the first
 * is created. Exactly one of the two is set.
 */
export interface ImportMatch {
  readonly recordId: string | null;
  readonly rowNo: number | null;
  readonly name: string;
  /** A property's city; null for a module with no address. */
  readonly city: string | null;
  readonly status: 'active' | 'inactive';
  readonly reasons: readonly ImportMatchReason[];
  readonly similarity: number | null;
}

export type ImportOutcome = 'committed' | 'skipped' | 'refused' | 'failed';

export interface ImportRow {
  readonly rowId: string;
  /** The row number a spreadsheet shows: the header is row 1. */
  readonly rowNo: number;
  readonly name: string | null;
  /** The module's key column (`ImportModuleSpec.keyField`): a Yardi ID, or null. */
  readonly key: string | null;
  readonly status: ImportRowStatus;
  readonly errors: readonly ImportFinding[];
  readonly warnings: readonly ImportFinding[];
  readonly matches: readonly ImportMatch[];
  /** Set once the run has executed: what happened to this row. */
  readonly outcome: ImportOutcome | null;
}

/** The run states a screen can be shown in (0086). */
export type ImportRunState =
  | 'draft'
  | 'uploading'
  | 'parsing'
  | 'rejected'
  | 'staged'
  | 'profiling'
  | 'mapped'
  | 'planned'
  | 'reviewed'
  | 'executing'
  | 'committed'
  | 'rolled_back'
  | 'cancelled'
  | 'expired';

export interface ImportRun {
  readonly runId: string;
  /** What this run creates — recorded on the run, never taken from a request. */
  readonly module: ImportModule;
  readonly version: number;
  readonly state: ImportRunState;
  readonly rows: readonly ImportRow[];
  readonly counts: {
    readonly total: number;
    readonly ready: number;
    readonly possibleDuplicate: number;
    readonly error: number;
    readonly withWarnings: number;
  };
  /** After execution: the four outcomes, which reconcile to `counts.total`. */
  readonly result: {
    readonly committed: number;
    readonly skipped: number;
    readonly refused: number;
    readonly failed: number;
  } | null;
  /** Why a rolled-back run wrote nothing, and the row that caused it if one did. */
  readonly failure: { readonly code: string; readonly rowNo: number | null } | null;
}

/** The records a just-committed run created, for the results screen's links. */
export interface ImportCommitted extends ImportRun {
  readonly created: readonly { readonly rowNo: number; readonly recordId: string }[];
}

/**
 * The confirmation (ADR-74 decision 3). The person's duplicate choices, the
 * rows they were TOLD would be skipped, and their acknowledgement of that list.
 * The server refuses unless the skipped list is exactly the one it computes: a
 * confirmation that names different rows was given about a different plan.
 */
export const importConfirmSchema = z
  .object({
    version: z.number().int().positive(),
    decisions: z
      .array(
        z
          .object({
            rowId: z.uuid(),
            include: z.boolean(),
            /** Required to include a possible duplicate — the create screen's own rule. */
            reason: z.string().trim().min(3).max(500).optional(),
          })
          .strict(),
      )
      .max(IMPORT_ROW_CAP),
    skippedRowIds: z.array(z.uuid()).max(IMPORT_ROW_CAP),
    acknowledgeSkipped: z.boolean(),
    idempotencyKey: z.string().regex(/^[A-Za-z0-9-]{16,128}$/),
  })
  .strict();
export type ImportConfirm = z.infer<typeof importConfirmSchema>;

export const importVersionSchema = z.object({ version: z.number().int().positive() }).strict();

/** Why a whole file was refused before any row was staged. */
export type ImportRefusalCode =
  | 'not_csv'
  | 'unreadable'
  | 'missing_name_column'
  | 'unknown_column'
  | 'duplicate_column'
  | 'lifecycle_column'
  | 'no_rows'
  | 'too_many_rows';

export interface ImportRefusal {
  readonly code: ImportRefusalCode;
  /** The column the refusal is about, when it is about one. */
  readonly header: string | null;
  readonly message: string;
}
